import * as amplitude from '@amplitude/analytics-browser';

import getStorage from '@zego/getStorage';
import { isBrowser } from '@zego/utils';

export const EVENT_TYPES = {
  LEAD: 'Lead',
  REGISTRATION: 'Registration',
  MAGIC_LINK: 'MAGIC_LINK',
  ADD_PAYMENT: 'AddPayment',
  PURCHASE: 'Purchase',
  CONFIGURE_PRODUCT: 'ConfigureProduct',
};

const SENSITIVE_PARAMS = ['email', 'password'];

let initialized = false;

function filterSensitiveParams(data) {
  return Object.keys(data)
    .filter(key => !SENSITIVE_PARAMS.includes(key))
    .reduce((obj, key) => {
      obj[key] = data[key];
      return obj;
    }, {});
}

// Modelled on zego-utils's urlProperties() (src/tracking/amplitude.ts) so
// events from this app use the same property names as apps that go through
// zego-utils, e.g. my-zego: every tracked event carries the current URL
// context, not just pageviews.
function urlProperties() {
  if (!isBrowser()) return {};
  return {
    $urlHash: window.location.hash,
    $urlPath: window.location.pathname,
    $urlQueryString: window.location.search,
  };
}

export function initTracking(apiKey) {
  if (initialized || !isBrowser() || !apiKey) return;
  initialized = true;

  amplitude
    .init(apiKey, undefined, {
      autocapture: {
        pageViews: false,
        attribution: true,
        sessions: false,
        formInteractions: false,
        fileDownloads: false,
      },
    })
    .promise.then(() => {
      if (process.env.NODE_ENV !== `production`) {
        // eslint-disable-next-line no-console
        console.log(`[Amplitude] Initialization successful`);
      }
    })
    .catch(error => {
      initialized = false;
      // eslint-disable-next-line no-console
      console.error(`[Amplitude] Initialization promise rejected:`, error);
    });
}

export function trackEvent(name, payload = {}) {
  const filteredPayload = filterSensitiveParams(payload);

  if (isBrowser()) {
    amplitude.track(name, { ...urlProperties(), ...filteredPayload });
  }

  if (process.env.NODE_ENV !== `production`) {
    // eslint-disable-next-line no-console
    console.log(`[Track Event] ${name}`, payload);
  }
}

// `name` was the Segment page name; Amplitude pageviews are always sent as a
// fixed "Loaded a Page" event, so it's discarded here rather than leaking
// into the event properties below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function trackPageview(location, { name, ...payload } = {}) {
  const storage = getStorage('session');

  const filteredPayload = filterSensitiveParams(payload);

  // $urlHash/$urlPath/$urlQueryString match zego-utils's urlProperties()
  // naming (src/tracking/amplitude.ts), sourced from the passed-in `location`
  // rather than window.location directly since callers (e.g. PageBootstrap)
  // derive it from router state.
  const mergedPayload = {
    $urlHash: location.hash,
    $urlPath: location.pathname,
    $urlQueryString: location.search,
    isSessionStartPage: false,
    ...filteredPayload,
  };

  if (storage && !storage.getItem('hasTrackedSessionStart')) {
    mergedPayload.isSessionStartPage = true;
    storage.setItem('hasTrackedSessionStart', true);
  }

  /* HERE BE DRAGONS
   * The Segment analytics.page function automatically collects
   * The document title (and other things) from the browser APIs.
   * Problem is that Next's API for updating things like document.title
   * don't trigger the change BEFORE we call this function, so it's always
   * one page behind.
   *
   * Our workaround is to do the actual pageview tracking one tick later, to
   * Give everything time to update properly.
   *
   * Once next.js fixes this, we can remove the setTimeout call.
   */
  setTimeout(() => {
    if (process.env.NODE_ENV !== `production`) {
      // eslint-disable-next-line no-console
      console.log(`[Track Pageview] ${location.pathname}`, mergedPayload);
    }
    if (isBrowser()) {
      // url/path match zego-utils's trackAmplitudePageView naming
      // (src/tracking/amplitude.ts).
      amplitude.track('Loaded a Page', {
        url: window.location.href,
        path: location.pathname,
        ...mergedPayload,
      });
    }
  }, 0);
}

export function identifyUser(id) {
  if (process.env.NODE_ENV !== `production`) {
    if (typeof id === 'object') {
      // eslint-disable-next-line no-console
      console.log(`[Identify User] (anonymous user)`);
      console.table(id);
    } else {
      // eslint-disable-next-line no-console
      console.log(`[Identify User] ${id}`);
    }
  }

  if (!isBrowser()) return;

  if (typeof id === 'object') {
    const identifyObj = new amplitude.Identify();
    Object.entries(filterSensitiveParams(id)).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        identifyObj.unset(key);
      } else {
        identifyObj.set(key, value);
      }
    });
    amplitude.identify(identifyObj);
  } else {
    amplitude.setUserId(id);
  }
}

export function reset() {
  if (process.env.NODE_ENV !== `production`) {
    // eslint-disable-next-line no-console
    console.log(`[Resetting user]`);
  }

  if (isBrowser()) {
    amplitude.reset();
  }
}
